import { Button, Form } from "antd";
import { Tooltip } from "antd-v5";
import { InfoIcon } from "components/Icons";
import _, { map, split, uniqBy } from "lodash";
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, generateFormFields } from "utils/formUtils";

import GenericMappingForm from "../GenericMappingForm";

const initialFormValues = [{
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  mappingList: [{ policyPrefix: undefined, hostIds: undefined }]
}];

const QualysPolicyComplianceMappingForm = forwardRef(
  function QualysPolicyComplianceMappingForm({ disableProjectFields = false, ...props }, ref) {
    const [form] = Form.useForm();
    const hostDetailsApiRef = useRef([]);

    const fetchHostDetails = useCallback((params) => {
      const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        login_id: props.loginConfigId,
        ...params
      });

      return {
          promise: promise.then(response => ({
            projects: uniqBy(response.projects, 'id')
          })),
          cancel
      };
    }, [props.loginConfigId, props.toolName]);

    const onValuesChange = useCallback((values, ...args) => {
      if (values.configurations) {
        const formValues = form.getFieldsValue();
        _.forEach(values.configurations, (configs, index) => {
          if (!configs || !configs.mappingList) {
            return;
          }
          _.forEach(configs.mappingList, (object, i) => {
            if (object && (object.policyPrefix || object.policyPrefix==="") && Object.keys(object).length === 1) {
              formValues.configurations[index].mappingList[i].hostIds = undefined;
            }
          });
        });
        form.setFieldsValue(formValues);
      }
    }, [form]);

    const getExtraFieldsData = useCallback((config, index) => {
      const { mappingList } = config;
      
      const policyPrefixes = [];
      const hostIdsGroups = [];
      
      _.forEach(mappingList, ({ policyPrefix, hostIds }) => {
        const prefixToAdd = (policyPrefix && policyPrefix.trim() !== '') ? policyPrefix : 'All';
        policyPrefixes.push(prefixToAdd);
        
        // Handle host IDs: if blank/empty, use "All", otherwise join with colons
        if (hostIds && Array.isArray(hostIds) && hostIds.length > 0) {
          hostIdsGroups.push(hostIds.join(':'));
        } else {
          hostIdsGroups.push('All'); // Use "All" if no host IDs selected
        }
      });
      
      return ({
        policyprefix: policyPrefixes.join(','),
        hostids: hostIdsGroups.join(',')
      });
    }, []);

    const mappingListRender = useCallback((listField) => {
      // Store the API method for host details in a ref
      let hostDetailsAPI = hostDetailsApiRef.current[listField.name];
      if (!hostDetailsAPI) {
        hostDetailsAPI = hostDetailsApiRef.current[listField.name] = ({ listFieldName: nestedListFieldName, ...params }) => {
          const allConfigs = form.getFieldsValue().configurations;
          const policyPrefix = allConfigs[listField.name]?.mappingList[nestedListFieldName]?.policyPrefix;
          if (!policyPrefix) {
            return { promise: Promise.resolve([]), cancel: () => {} };
          }
          return fetchHostDetails({ ...params, name: policyPrefix });
        }
      }

      const __fields = [{
        name: 'policyPrefix',
        type: FieldTypes.INPUT,
        disabled: disableProjectFields,
        colSpan: 10,
        props: {
          placeholder: "Policy Prefix(Leave blank to select all)",
          autoFocus: !disableProjectFields,
        },
        required: false
      }, {
        name: 'hostIds',
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        disabled: disableProjectFields,
        colSpan: 10,
        props: {
          placeholder: "Leave blank to select all",
          api: hostDetailsAPI,
          mode: "multiple",
          setExtraProps: (field, nestedListField) => {
            const allConfigs = form.getFieldsValue().configurations;
            const policyPrefix = allConfigs[listField.name]?.mappingList[nestedListField.name]?.policyPrefix;
            return {
              showValueOnload: false,
              resetOptionsProp: policyPrefix
            }
          }
        },
      }, {
        name: "actions",
        colSpan: 2,
        render: ({ remove, name, fields, add }) => {
          const isLastEntry = name === fields.length - 1;
          const showRemoveButton = fields.length > 1;
          return (
            <div style={{ display: 'flex', gap: '4px' }}>
              {showRemoveButton && (
                <Button 
                  type='dashed' 
                  onClick={() => { remove(name) }}
                >
                  -
                </Button>
              )}
              {isLastEntry && (
                <Button 
                  type='dashed' 
                  onClick={() => add({ policyPrefix: undefined, hostIds: undefined })} 
                  disabled={disableProjectFields}
                >
                  +
                </Button>
              )}
            </div>
          );
        }
      }]

      return (
        <div className='m-b-sm'>
          <Form.List name={[listField.name, "mappingList"]}>
            {(fields, { add, remove }) => {
              return (
                <div>
                  {map(fields, field => {
                    return generateFormFields(__fields, 9, { ...field, add, remove, fields }, field.name);
                  })}
                </div>
              )
            }}
          </Form.List>
        </div>
      )
    }, [disableProjectFields, form, fetchHostDetails]);

    const fields = useMemo(() => [{
      name: "label",
      type: FieldTypes.LABEL,
      label: (
        <>
          Enter Qualys Policy
          <Tooltip title="Enter the Policy Name Prefix or Full Policy Name">
            <InfoIcon className="m-l-xs" />
          </Tooltip>
        </>
      ),
      style: { marginBottom: -44 },
      colSpan: 10
    }, {
      name: "label",
      type: FieldTypes.LABEL,
      label: (
        <>
        Select Hosts
        </>
      ),
      style: { marginBottom: -44 },
      colSpan: 10
    }, {
      name: (listField) => [listField.name, "mappingList"],
      render: mappingListRender
    }, {
      type: FieldTypes.DIVIDER
    }], [mappingListRender]);

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
      const { policyprefix, hostids } = config;
      
      if (policyprefix || hostids) {
        const policyPrefixArray = policyprefix ? split(policyprefix, ',') : [];
        const hostIdsArray = hostids ? split(hostids, ',') : [];
        
        const mappings = map(policyPrefixArray, (prefix, index) => {
          // Handle prefix: if "All", convert back to undefined/empty
          const actualPrefix = (prefix && prefix.trim() === 'All') ? undefined : prefix;
          
          // Get corresponding host IDs for this prefix (colon-separated)
          const hostIdsGroup = hostIdsArray[index] || '';
          let hostIdsForPrefix = [];
          
          // Handle host IDs: if "All", convert back to undefined, otherwise split by colon
          if (hostIdsGroup && hostIdsGroup.trim() === 'All') {
            hostIdsForPrefix = undefined; // "All" means no specific selection
          } else if (hostIdsGroup && hostIdsGroup.trim() !== '') {
            hostIdsForPrefix = split(hostIdsGroup, ':').filter(Boolean);
          }
          
          return { 
            policyPrefix: actualPrefix, 
            hostIds: hostIdsForPrefix && hostIdsForPrefix.length > 0 ? hostIdsForPrefix : undefined
          };
        });

        // If we have host IDs but no prefixes, create a mapping with empty prefix
        if (hostIdsArray.length > 0 && policyPrefixArray.length === 0) {
          const allHostIds = hostIdsArray.join(':').split(':').filter(Boolean);
          mappings.push({
            policyPrefix: undefined,
            hostIds: allHostIds.length > 0 ? allHostIds : undefined
          });
        }

        return ({
          mappingList: mappings.length > 0 ? mappings : [{ policyPrefix: undefined, hostIds: undefined }],
        });
      }
    }, []);

    return (
      <GenericMappingForm
        ref={ref}
        getExtraFieldsData={getExtraFieldsData}
        parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
        extraFields={fields}
        form={form}
        formDefaultValues={initialFormValues}
        newRowDefaultValue={initialFormValues[0]}
        onValuesChange={onValuesChange}
        {...props}
      />
    );
  },
);

export default QualysPolicyComplianceMappingForm;